import { useBookingLocale } from '../../hooks/useBookingLocale';
import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { BookingStatus } from '../../../__generated__/graphql';
import { Button } from '../../components/button';
import { Alert, Badge, LoadingBlock, Muted, Stack, Strong } from '../../components/ui';
import {
  useStayBookingAsGuestQuery,
  useWithdrawBookingRequestMutation,
} from '../../services/stayService/__generated__/queries.generated';
import { stayErrorMessage } from '../StayHostPage/stayErrors';
import { ChangeStayModal } from './ChangeStayModal';
import { useOpenBookingChat } from './useOpenBookingChat';
import {
  CheckInDetailsSection,
  StayRequestSection,
  StayReviewSection,
} from './StayLifecycleSections';
import { formatMoney } from '../StayGuestPage/stayQuoteLines';
import {
  CancelStayModal,
  Columns,
  Detail,
  Page,
  PolicyText,
  PriceBreakdown,
  Section,
  SectionHeading,
  SideCard,
  StayBookingSummary,
  Title,
  bookingSubtitle,
} from './StayBookingComponents';
import {
  guestCountsLabel,
  paidTotalLabel,
  receiptQuote,
  stayDateTime,
  stayStatusView,
} from './stayBooking';

/**
 * A guest's stay (spec 016 W18): status, check-in and checkout, the address once the
 * stay is confirmed, check-in details from 48 hours before, the receipt, withdraw a
 * request, change or cancel under the policy, then the review and the Resolution Center.
 */
export default function StayTripPage() {
  const { bookingId = '' } = useParams<{ bookingId: string }>();
  // Spec 028: the currency the stay was booked in.
  const tripLocale = useBookingLocale(bookingId, 'guest');
  const navigate = useNavigate();
  const [cancelOpen, setCancelOpen] = useState(false);
  const [changeOpen, setChangeOpen] = useState(false);
  const chat = useOpenBookingChat();
  const { data, loading, error, refetch } = useStayBookingAsGuestQuery({
    variables: { id: bookingId },
    skip: !bookingId,
    fetchPolicy: 'cache-and-network',
  });
  const [withdraw, { loading: withdrawing }] = useWithdrawBookingRequestMutation();
  const booking = data?.getBookingAsGuest;

  if (loading && !booking) return <LoadingBlock label="Loading your stay" />;
  if (!booking) {
    return (
      <Page>
        <Alert tone="error" title="Stay not found">
          {error ? stayErrorMessage(error) : 'This stay does not exist.'}
        </Alert>
      </Page>
    );
  }

  const zone = booking.car?.location?.timezone;
  const view = stayStatusView(booking, 'guest');
  const location = booking.car?.location;
  const confirmed =
    booking.status === BookingStatus.Booked || booking.status === BookingStatus.Completed;
  const canCancel = booking.status === BookingStatus.Booked && Date.now() < booking.endAt;
  const hostName = booking.host?.firstName || booking.host?.displayName || 'your host';

  const withdrawRequest = async () => {
    try {
      await withdraw({ variables: { bookingId } });
      toast.success('Request withdrawn. The hold on your card was released.');
    } catch (e) {
      toast.error(stayErrorMessage(e, 'The request could not be withdrawn'));
    }
  };

  return (
    <Page data-testid="stay-trip-page">
      <Muted $size="sm">
        <Link to="/guest/mybookings">Trips</Link>
      </Muted>
      <Title>{booking.car?.name || 'Your stay'}</Title>
      <Stack $gap={4} style={{ marginTop: 8 }}>
        <div>
          <Badge tone={view.tone} data-testid="stay-trip-status">
            {view.label}
          </Badge>
        </div>
        <Muted>{view.description}</Muted>
      </Stack>
      <Columns>
        <div>
          <Section>
            <SectionHeading>Check-in and checkout</SectionHeading>
            <Stack $gap={4}>
              <Detail label="Check-in">{stayDateTime(booking.startAt, zone)}</Detail>
              <Detail label="Checkout">{stayDateTime(booking.endAt, zone)}</Detail>
              <Detail label="Guests">{guestCountsLabel(booking.guestCounts)}</Detail>
            </Stack>
          </Section>

          <Section>
            <SectionHeading>Where you are staying</SectionHeading>
            {confirmed && location?.address ? (
              <Stack $gap={4}>
                <Strong data-testid="stay-trip-address">
                  {[location.address, location.city, location.state, location.zipCode]
                    .filter(Boolean)
                    .join(', ')}
                </Strong>
                {location.latitude && location.longitude ? (
                  <Muted $size="sm">
                    <a
                      href={`https://www.google.com/maps/dir/?api=1&destination=${location.latitude},${location.longitude}`}
                      target="_blank"
                      rel="noreferrer"
                    >
                      Get directions
                    </a>
                  </Muted>
                ) : null}
              </Stack>
            ) : (
              <Muted>
                {[location?.city, location?.state].filter(Boolean).join(', ')}. The exact address
                appears here once the stay is confirmed.
              </Muted>
            )}
          </Section>

          <CheckInDetailsSection booking={booking} viewer="guest" />
          <StayReviewSection booking={booking} viewer="guest" />

          {booking.requestMessage ? (
            <Section>
              <SectionHeading>Your message to {hostName}</SectionHeading>
              <Muted>{booking.requestMessage}</Muted>
            </Section>
          ) : null}

          <Section>
            <SectionHeading>Cancellation policy</SectionHeading>
            <PolicyText policy={booking.cancellationPolicySnapshot} />
          </Section>

          <StayRequestSection booking={booking} viewer="guest" />

          <Section>
            <Stack $gap={4}>
              {booking.status === BookingStatus.Requested && (
                <Button
                  theme="outlined"
                  text="Withdraw request"
                  isLoading={withdrawing}
                  onClick={withdrawRequest}
                  dataTestId="stay-withdraw"
                />
              )}
              {booking.status === BookingStatus.Pending && booking.car?.id && (
                <Button
                  text="Finish checkout"
                  onClick={() => navigate(`/stays/book/${booking.car?.id}/${booking.id}`)}
                />
              )}
              {booking.status === BookingStatus.CardDeclined && booking.car?.id && (
                <Button
                  text="Book again with another card"
                  onClick={() => navigate(`/stays/${booking.car?.id}`)}
                />
              )}
              {canCancel && (
                <Button
                  theme="outlined"
                  text="Change reservation"
                  onClick={() => setChangeOpen(true)}
                  dataTestId="stay-change"
                />
              )}
              {canCancel && (
                <Button
                  theme="outlined"
                  text="Cancel stay"
                  onClick={() => setCancelOpen(true)}
                  dataTestId="stay-cancel"
                />
              )}
              <Button
                theme="text"
                text="Message the host"
                isLoading={chat.loading}
                onClick={() => chat.open(booking)}
                dataTestId="stay-message-host"
              />
            </Stack>
          </Section>
        </div>

        <SideCard>
          <Stack $gap={4}>
            <StayBookingSummary
              name={booking.car?.name}
              imageUrl={booking.car?.images?.[0]?.imageUrl}
              subtitle={bookingSubtitle(booking)}
            />
            <PriceBreakdown
              quote={receiptQuote({
                ...booking.receipt,
                // After a cancellation the receipt total is what was kept; show what was paid.
                totalPrice:
                  Number(booking.receipt.totalPrice ?? 0) + Number(booking.receipt.balance ?? 0),
              })}
              totalLabel={paidTotalLabel(booking.status, tripLocale.currency)}
              footer={
                <>
                  {booking.receipt.balance ? (
                    <Muted $size="sm">
                      Refunded: {formatMoney(Number(booking.receipt.balance))}
                    </Muted>
                  ) : null}
                  {booking.receipt.last4digitOfCard &&
                  booking.receipt.last4digitOfCard !== 'xxxx' ? (
                    <Muted $size="sm">Card ending in {booking.receipt.last4digitOfCard}</Muted>
                  ) : null}
                  {booking.shortId ? (
                    <Muted $size="sm">Confirmation code {booking.shortId}</Muted>
                  ) : null}
                </>
              }
            />
          </Stack>
        </SideCard>
      </Columns>
      <ChangeStayModal
        booking={booking}
        isOpen={changeOpen}
        onClose={() => setChangeOpen(false)}
        onChanged={() => {
          setChangeOpen(false);
          void refetch();
        }}
      />
      <CancelStayModal
        bookingId={booking.id}
        isOpen={cancelOpen}
        onClose={() => setCancelOpen(false)}
        onCancelled={() => {
          setCancelOpen(false);
          void refetch();
        }}
      />
    </Page>
  );
}
